// import { AngularFireAuthGuard, redirectUnauthorizedTo } from '@angular/fire/compat/auth-guard';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule} from '@angular/material/tooltip';
import { NgOptimizedImage } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { StableTextDirective } from '@app/directives/stable-text';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MAT_DATE_FORMATS, MatRippleModule } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
} from '@angular/material-moment-adapter';
import { Router, RouterModule, Routes } from '@angular/router';

import { QRCodeModule } from 'angularx-qrcode';
import { TranslocoModule } from '@jsverse/transloco';

import * as Sentry from '@sentry/angular';

import { AnonGuard } from './guards/anon.guard';
import { AppComponent } from './app.component';
import { authGuard } from './guards/auth.guard';
import { BackendTranslatePipe } from './pipes/backend-translate.pipe';
import { BrandingLogosComponent } from '@app/components/branding-logos/branding-logos.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { DictionaryComponent } from './pages/speech/dictionary/dictionary.component';
import { displayGuard } from './guards/display.guard';
import { DisplaySettingsComponent } from './components/display-settings/display-settings.component';
import { DynamicThemeDirective } from './directives/dynamic-theme.directive';
import { environment } from '../environments/environment';
import { HeaderComponent } from './shared/components/header/header.component';
import { HubSelectorComponent } from './pages/hub-selector/hub-selector.component';
import { JoinCodeInputComponent } from '@app/pages/join/join-code-input.component';
import { JoinComponent } from '@app/pages/join/join.component';
import { LanguageSettingDialogComponent } from './components/language-setting-dialog/language-setting-dialog.component';
import { LiveTranslatePipe } from './pipes/live-translate.pipe';
import { LoginComponent } from './pages/login/login.component';
import { MicrophoneBarComponent } from './components/microphone-bar/microphone-bar.component';
import { MicrophoneComponent } from './components/microphone/microphone.component';
import { MicrophoneSettingsComponent } from './components/microphone-settings/microphone-settings.component';
import { NoSleepService } from './services/no-sleep.service';
import { PersonalizeDialogComponent } from './components/personalize-dialog/personalize-dialog.component';
import { ShareDialogComponent } from './components/share-dialog/share.component';
import { SpeechBubbleTextDirective } from './directives/speech-bubble-text.directive';
import { ViewportWidthDirective } from './directives/speech-viewport.directive';

import {
  CustomSnackbarComponent,
  ScrollBackDownButtonComponent,
} from './components/scroll-back-down-button/scroll-back-down-button.component';

import { SharedSessionAuthService } from './services/shared-session-auth.service';
import { SpeechBubbleComponent } from './pages/speech/speech-bubble/speech-bubble.component';
import { SpeechComponent } from './pages/speech/speech.component';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { TranslateService } from './services/translate.service';

import {
  SpeakseeAngularCoreModule,
  SpeakseeLoginModule,
} from '@speaksee/speaksee-angular-core';
import { DeveloperDialogComponent } from './components/developer-dialog/developer-dialog.component';
import { Error403Component } from './pages/error/403/403.component';

import { AvViewComponent } from './features/av-view/av-view.component';
import { avViewResolver } from './features/av-view/av-view.resolver';
import { InformationBannerComponent } from './components/information-banner/information-banner.component';
import { LicensesResolver } from './modules/license/license.resolver';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { SessionResolver } from './modules/session/session.resolver';
import { ToastrModule } from 'ngx-toastr';
import { TranslocoRootModule } from './transloco-root.module';

// DEPRECATION NOTE:
// Class and injection token guards and resolvers are deprecated.
// the interfaces like CanActivate, CanDeactivate, etc. will be deleted in a
// future release of Angular
//
// More info:
// https://angular.io/guide/deprecations#router-class-and-injectiontoken-guards-and-resolvers
const appRoutes: Routes = [
  { path: 'join', component: JoinComponent },
  { path: 'join/:code', component: JoinComponent },
  {
    path: 'speech/:id',
    component: SpeechComponent,
    resolve: {
      session: SessionResolver,
    },
    canActivate: [authGuard],
  },
  { path: 'speech', component: SpeechComponent },
  { path: 'dictionary', component: DictionaryComponent },
  { path: 'login', component: LoginComponent, canActivate: [AnonGuard] },
  {
    path: 'microphone-kits',
    component: HubSelectorComponent,
    resolve: {
      licenses: LicensesResolver,
    },
    canActivate: [authGuard],
  },
  {
    path: 'av-view/:id',
    component: AvViewComponent,
    resolve: {
      resolvedData: avViewResolver,
    },
    canActivate: [displayGuard, authGuard],
  },
  { path: '403', component: Error403Component, canActivate: [AnonGuard] },
  {
    path: 'access-restricted',
    loadComponent: () => import('./pages/access-restricted/access-restricted.component').then(m => m.AccessRestrictedComponent)
  },
  { path: '**', redirectTo: '/login' },
];

@NgModule({
  declarations: [
    AppComponent,
    ConfirmDialogComponent,
    CustomSnackbarComponent,
    DictionaryComponent,
    DisplaySettingsComponent,
    MicrophoneSettingsComponent,
    SpeechBubbleTextDirective,
    DynamicThemeDirective,
    HeaderComponent,
    HubSelectorComponent,
    JoinCodeInputComponent,
    JoinComponent,
    LanguageSettingDialogComponent,
    BackendTranslatePipe,
    LiveTranslatePipe,
    LoginComponent,
    MicrophoneBarComponent,
    MicrophoneComponent,
    PersonalizeDialogComponent,
    ScrollBackDownButtonComponent,
    ShareDialogComponent,
    SpeechBubbleComponent,
    SpeechComponent,
    Error403Component,
    DeveloperDialogComponent,
    ViewportWidthDirective,
    InformationBannerComponent,
  ],
  imports: [
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    BrowserAnimationsModule,
    BrowserModule,
    BrandingLogosComponent,
    NgOptimizedImage,
    FormsModule,
    HttpClientModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatMomentDateModule,
    MatRippleModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatFormFieldModule,
    NgxSliderModule,
    QRCodeModule,
    ReactiveFormsModule,
    RouterModule.forRoot(appRoutes, {}),
    ScrollingModule,
    StableTextDirective,
    TranslocoModule,
    //
    SpeakseeAngularCoreModule.forRoot({
      apiBaseUrl: environment.speaksee.apiUrl,
      services: {
        auth: {
          version: 'v1',
        },
        order: {
          version: 'v1',
        },
        settings: {
          version: 'v1',
        },
      },
    }),
    SpeakseeLoginModule,
    TranslocoRootModule,
    ToastrModule.forRoot({
      timeOut: 1500,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      tapToDismiss: true,
      progressBar: true,
      newestOnTop: false,
      resetTimeoutOnDuplicate: true,
      toastClass: 'custom-toast ngx-toastr'
    }),
  ],
  providers: [
    TranslateService,
    NoSleepService,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        display: { dateInput: 'DD/MM/YYYY' },
      },
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      // Renew the shared-session guest token on app bootstrap so a reload / reopened
      // tab boots with a valid token (and arms the proactive refresh timer). Never
      // rejects: on failure the route guard sends the user to /join to re-scan.
      provide: APP_INITIALIZER,
      useFactory: (sharedAuth: SharedSessionAuthService) => () =>
        sharedAuth.isActive()
          ? sharedAuth.ensureValidToken().catch(() => false)
          : Promise.resolve(),
      deps: [SharedSessionAuthService],
      multi: true,
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
